
import { Component, Input, Output, EventEmitter, signal, computed, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { SignaturePadComponent } from '@app/common-codes/components/nested-components/signature-pad/signature-pad.component';
import { TranslatableItem } from '@app/common-codes/components/nested-components/translatable-entity-modal/translatable-entity.interface';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { SafeHtmlPipe } from '@app/common-codes/components/directives/SafeHtmlPipe';

export interface EngagementViewerAcceptEvent {
    accepted: boolean;
    signatureFile?: File;
    checkedItems: number[]; // IDs des items cochés
}

@Component({
    selector: 'app-engagement-viewer',
    standalone: true,
    imports: [FormsModule, TranslatePipe, SafeHtmlPipe, SignaturePadComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './engagement-viewer.component.html'
})
export class EngagementViewerComponent implements OnInit, OnChanges {

    /** Items (engagements, quizzes, covids, buzzers) avec leurs translations */
    @Input({ required: true }) items: TranslatableItem[] = [];


    /** typeDeTraduction pour le titre (default: 1) */
    titleTranslationType: number = 1;

    /** typeDeTraduction pour le contenu HTML (default: 2) */
    contentTranslationType: number = 2;


    /** Afficher la signature */
    @Input() showSignature: boolean = true;

    /** Rendre la signature obligatoire pour accepter */
    @Input() requireSignature: boolean = true;

    @Output() accepted = new EventEmitter<EngagementViewerAcceptEvent>();
    // @Output() refused = new EventEmitter<void>();
    @Output() signatureChange = new EventEmitter<File | null>();

    expandedItemId = signal<number | null>(null);
    checkedItems = signal<Set<number>>(new Set());
    allAccepted = signal(false);
    signatureFile: File | null = null;

    private currentLangKey: string = 'en';


    private currentLanguageId: number | null = null;

    allChecked = computed(() => {
        const checked = this.checkedItems();
        return this.items.length > 0 && this.items.every(item => checked.has(item.id));
    });

    // canAccept = computed(() => {
    //     if (!this.allAccepted()) return false;
    //     if (this.requireSignature && !this.signatureFile) return false;
    //     return true;
    // });

    constructor(private translationService: TranslationService) { }

    ngOnInit(): void {
        this.currentLangKey = this.translationService.currentLanguage;
        // Écouter les changements de langue
        this.translationService.currentLanguage$.subscribe(lang => {
            this.currentLangKey = lang;
        });
        if (this.items.length > 0) {
            this.expandedItemId.set(this.items[0].id);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['items']) {
            this.checkedItems.set(new Set());
            this.allAccepted.set(false);

            if (this.items.length > 0) {
                this.expandedItemId.set(this.items[0].id);
            }
        }
    }

    private resolveLanguageId(): void {
        const currentLang = this.translationService.currentLanguage;
        // Tenter de résoudre le languageId depuis les translations disponibles
        for (const item of this.items) {
            for (const t of (item.translations || [])) {
                if (t.language?.key === currentLang) {
                    this.currentLanguageId = t.fK_Language;
                    return;
                }
            }
        }
        // Fallback: prendre la première langue disponible
        if (this.items.length > 0 && this.items[0].translations?.length) {
            this.currentLanguageId = this.items[0].translations[0].fK_Language;
        }
    }

    /** Retourne le titre (typeDeTraduction=1) d'un item dans la langue courante */
    getTitle(item: TranslatableItem): string {
        const translations = item.translations || [];
        // Chercher dans la langue courante
        let t = translations.find(
            tr => tr.typeDeTraduction === this.titleTranslationType && tr.language?.key === this.currentLangKey
        );
        // Fallback anglais
        if (!t && this.currentLangKey !== 'en') {
            t = translations.find(
                tr => tr.typeDeTraduction === this.titleTranslationType && tr.language?.key === 'en'
            );
        }
        // Dernier fallback
        if (!t) {
            t = translations.find(tr => tr.typeDeTraduction === this.titleTranslationType);
        }
        return t?.translationValue || item.description || `Item #${item.id}`;
    }

    /** Retourne le contenu HTML (typeDeTraduction=2) d'un item dans la langue courante */
    getContent(item: TranslatableItem): string {
        const translations = item.translations || [];
        let t = translations.find(
            tr => tr.typeDeTraduction === this.contentTranslationType && tr.language?.key === this.currentLangKey
        );
        if (!t && this.currentLangKey !== 'en') {
            t = translations.find(
                tr => tr.typeDeTraduction === this.contentTranslationType && tr.language?.key === 'en'
            );
        }
        if (!t) {
            t = translations.find(tr => tr.typeDeTraduction === this.contentTranslationType);
        }
        return t?.translationValue || '';
    }

    toggleExpand(itemId: number): void {
        this.expandedItemId.update(current => current === itemId ? null : itemId);
    }

    isExpanded(itemId: number): boolean {
        return this.expandedItemId() === itemId;
    }

    toggleCheck(itemId: number): void {
        let wasAdded = false;
        this.checkedItems.update(current => {
            const next = new Set(current);
            if (next.has(itemId)) {
                next.delete(itemId);
            } else {
                next.add(itemId);
                wasAdded = true;
            }
            return next;
        });

        // Auto-ouvrir l'accordéon suivant si on vient de cocher
        if (wasAdded) {
            const currentIndex = this.items.findIndex(i => i.id === itemId);
            if (currentIndex >= 0 && currentIndex < this.items.length - 1) {
                const nextItem = this.items[currentIndex + 1];
                this.expandedItemId.set(nextItem.id);
            } else {
                this.expandedItemId.set(null);
            }
        }

        if (this.allChecked() === false)
            this.onAllAcceptedChange(false);
    }

    isChecked(itemId: number): boolean {
        return this.checkedItems().has(itemId);
    }

    onAllAcceptedChange(checked: boolean): void {
        this.allAccepted.set(checked);
        this.accepted.emit({
            accepted: checked,
            signatureFile: this.signatureFile ?? undefined,
            checkedItems: Array.from(this.checkedItems())
        });
    }

    onSignatureChange(file: File | null): void {
        this.signatureFile = file;
        this.signatureChange.emit(file);
    }

}